"use client";

import { EdgenModalCreateAble } from "@components/EdgenModal";
import { callEdgenModalWhenRootReadyAsync } from "@components/EdgenModal/waitForEdgenModalRoot";
import {
  pickPortfolioFiles,
  resetPortfolioFilePickerHostState,
} from "@/src/hooks/onboarding/portfolio";
import { PortfolioScreenshotModalContent } from "./PortfolioScreenshotModalContent";
import type {
  OpenPortfolioScreenshotModalParams,
  PortfolioScreenshotModalResult,
  PortfolioUploadType,
} from "./types";

/**
 * 防重入：同一时刻只允许一个 modal 流程（含开弹窗前的 pick）。
 * pick  hang 时第二次点击会复用同一 promise；取消后 `pendingModal` 置 null。
 */
let pendingModal: Promise<PortfolioScreenshotModalResult> | null = null;

/**
 * 持仓识别弹窗入口（`request_portfolio` Upload、Setting 调试按钮等）。
 *
 * 默认 `pickOnOpen: true`：先 `pickPortfolioFiles`（三端策略分发），
 * 用户取消则返回 `{ action: "cancel" }` 且不打开弹窗。
 *
 * Android 在 pick 前 `resetPortfolioFilePickerHostState`，避免上次 session 卡死。
 *
 * @see apps/token-seek/features/20260610-portfolio-file-pick-platform-strategy/SPEC.md
 */
export const openPortfolioScreenshotModal = (
  params: OpenPortfolioScreenshotModalParams = {},
): Promise<PortfolioScreenshotModalResult> => {
  if (pendingModal) {
    return pendingModal;
  }

  const uploadType: PortfolioUploadType = params.uploadType ?? "image";
  const pickOnOpen = params.pickOnOpen ?? true;

  pendingModal = (async () => {
    let initialFiles: File[] | undefined;

    if (pickOnOpen) {
      resetPortfolioFilePickerHostState();
      let picked: File[] | null = null;
      picked = await pickPortfolioFiles(uploadType);
      if (!picked?.length) {
        resetPortfolioFilePickerHostState();
      }
      if (!picked?.length) {
        pendingModal = null;
        return { action: "cancel" } as const;
      }
      initialFiles = picked;
    }

    return callEdgenModalWhenRootReadyAsync(
      () =>
        new Promise<PortfolioScreenshotModalResult>((resolve) => {
          let settled = false;
          const finish = (result: PortfolioScreenshotModalResult) => {
            if (settled) {
              return;
            }
            settled = true;
            pendingModal = null;
            EdgenModalCreateAble.end(false);
            resolve(result);
          };

          EdgenModalCreateAble.call({
            children: (
              <PortfolioScreenshotModalContent
                uploadType={uploadType}
                initialFiles={initialFiles}
                onClose={finish}
              />
            ),
            className:
              "[--modal-radius:0px] [--modal-width:100%] [--modal-height:100dvh] [--modal-min-height:0px] [--modal-max-height:none] md:[--modal-radius:16px] md:[--modal-width:400px] md:[--modal-height:auto] md:[--modal-min-height:300px] md:[--modal-max-height:90vh]",
            contentStyle: {
              borderRadius: "var(--modal-radius)",
              width: "var(--modal-width)",
              height: "var(--modal-height)",
              minHeight: "var(--modal-min-height)",
              maxHeight: "var(--modal-max-height)",
              overflow: "hidden",
              padding: 0,
            },
            styles: {
              body: {
                height: "var(--modal-height)",
                minHeight: "var(--modal-min-height)",
                maxHeight: "var(--modal-max-height)",
              },
            },
            containerStyle: {
              height: "100%",
              width: "100%",
              maxWidth: "100%",
              maxHeight: "100%",
            },
            titleStyle: {
              minHeight: 0,
            },
            showClose: false,
            destroyOnClose: true,
          });
        }),
    );
  })().catch((error) => {
    pendingModal = null;
    throw error;
  });

  return pendingModal;
};
